// Purpose: Toggle switch primitive. Used in admin Settings for boolean
// config (require-MFA-for-admin, allow-external-dropboxes, etc.) and
// in user preference rows. Follows native checkbox a11y.

import type { ReactNode } from 'react';
import { forwardRef, useId } from 'react';

export interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  id?: string;
  className?: string;
}

export const Toggle = forwardRef<HTMLInputElement, ToggleProps>(function Toggle(
  { checked, onChange, label, description, disabled, id: externalId, className },
  ref
) {
  const generatedId = useId();
  const id = externalId ?? generatedId;
  return (
    <label
      htmlFor={id}
      className={[
        'flex items-start gap-4 cursor-pointer',
        disabled ? 'opacity-50 cursor-not-allowed' : '',
        className ?? '',
      ].join(' ')}
    >
      <span className="relative inline-block w-10 h-6 flex-shrink-0 mt-0.5">
        <input
          ref={ref}
          id={id}
          type="checkbox"
          role="switch"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          className="peer sr-only"
        />
        <span
          aria-hidden
          className={[
            'absolute inset-0 rounded-full transition-colors duration-base ease-out',
            'border border-border-default',
            checked ? 'bg-accent-primary border-accent-primary' : 'bg-bg-tertiary',
          ].join(' ')}
        />
        <span
          aria-hidden
          className={[
            'absolute top-1 left-1 w-4 h-4 rounded-full bg-white',
            'transition-transform duration-base ease-out',
            checked ? 'translate-x-4' : 'translate-x-0',
            'shadow-sm',
          ].join(' ')}
        />
      </span>
      {(label || description) && (
        <span className="flex flex-col gap-1">
          {label ? <span className="text-body-m text-fg-primary">{label}</span> : null}
          {description ? <span className="text-body-s text-fg-muted">{description}</span> : null}
        </span>
      )}
    </label>
  );
});
